import React from "react";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import { get, map, max, sum, sortBy, filter } from "lodash";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import DashboardCard from "../DashboardCard";
import CustomSelect from "../../Common/CustomSelect";
import MdcChartValue from "./MdcChartValue";

import colors from "../../../assets/style/colors";

const views = {
  bySite: "by_sites",
  byCarrier: "by_carriers",
};

const metrics = {
  nbVisits: "nb_visits",
};

const styles = {
  radioLabel: {
    fontWeight: 500,
    color: colors.black87,
    fontSize: "1em",
    marginRight: "20px",
  },
  chartContainer: {
    height: "100%",
    maxHeight: "329px",
    marginTop: "15px",
    overflowY: "scroll",
  },
  chartGrid: {
    position: "absolute",
    height: "340px",
    bottom: 0,
    left: "20px",
    maxWidth: "calc(100% - 36px)",
  },
  chartGridItem: {
    borderTop: "none",
    borderRight: "none",
    borderLeft: `1px solid ${colors.greyBadgeBg}`,
    borderBottom: `1px solid ${colors.greyBadgeBg}`,
    height: "100%",
  },
  scaleNb: {
    height: "16px",
    fontSize: "0.857em",
    color: colors.black54,
    backgroundColor: colors.white,
  },
};

const MdcChart = (props) => {
  const { classes, language } = props;
  const [metric, setMetric] = React.useState(metrics.nbVisits);
  const [view, setView] = React.useState(views.bySite);
  const [containerWidth, setContainerWidth] = React.useState(275);

  React.useEffect(() => {
    window.addEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    handleResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.getElementById("mdcChart")]);

  const handleResize = () => {
    const width = get(document.getElementById("mdcChart"), ["offsetWidth"]);
    setContainerWidth(width - 52);
  };

  const barColors = [colors.dashboardDarkBlue, colors.dashboardLightBlue];
  const secondLevel = view === views.bySite ? "carriers" : "sites";

  const data = sortBy(
    filter(
      get(props, ["data", "card7", metric, view]),
      (item) => -sum(map(get(item, [secondLevel]), (c) => c.total)),
      (item) => sum(map(get(item, [secondLevel]), (c) => c.total)) !== 0
    )
  );

  const maxValue = max(
    map(data, (item) => sum(map(get(item, [secondLevel]), (c) => c.total)))
  );

  const gridNb = 20;
  const gridSpace = (containerWidth - 39) / gridNb;

  let maxScaleValue = maxValue || 20;
  while (maxScaleValue % 20 !== 0) {
    maxScaleValue++;
  }

  return (
    <DashboardCard id="mdcChart">
      <Box style={{ marginLeft: "12px" }}>
        {I18n.get("dashboard_chooseMetrics")}
      </Box>
      <Grid container alignItems="center">
        <Grid
          item
          style={{ marginRight: "60px", width: "230px", marginLeft: "12px" }}
        >
          <CustomSelect
            name="metric"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            options={map(metrics, (v, k) => ({
              id: v,
              name: I18n.get(`dashboard_${v}`),
            }))}
          />
        </Grid>
        <Grid item>
          <RadioGroup
            row
            name="view"
            value={view}
            onChange={(e) => setView(e.target.value)}
          >
            <FormControlLabel
              value={views.bySite}
              control={<Radio color={"primary"} />}
              label={I18n.get("dashboard_bySite")}
              classes={{ label: classes.radioLabel }}
            />
            <FormControlLabel
              value={views.byCarrier}
              control={<Radio color={"primary"} />}
              label={I18n.get("dashboard_byCarrier")}
              classes={{ label: classes.radioLabel }}
            />
          </RadioGroup>
        </Grid>
      </Grid>
      <Grid
        container
        justify="center"
        alignItems="center"
        className={classes.chartContainer}
      >
        <Grid
          item
          style={{ width: containerWidth, height: "309px", zIndex: 100 }}
        >
          {map(data, (item, index) => (
            <MdcChartValue
              item={item}
              key={index}
              secondLevel={secondLevel}
              barColors={barColors}
              maxValue={maxScaleValue}
              language={language}
            />
          ))}
        </Grid>
      </Grid>
      <Grid item className={classes.chartGrid}>
        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{ height: "100%" }}
        >
          <Grid item style={{ height: "100%" }}>
            <Grid container style={{ height: "100%" }}>
              {map(new Array(gridNb), (i, index) => (
                <Grid
                  item
                  className={classes.chartGridItem}
                  key={index}
                  style={{
                    width: `${gridSpace}px`,
                    borderRight:
                      index === gridNb - 1
                        ? `1px solid ${colors.greyBadgeBg}`
                        : "none",
                  }}
                />
              ))}
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              wrap="nowrap"
              style={{ marginLeft: "-4px" }}
              alignItems="center"
            >
              {map(new Array(gridNb + 1), (i, index) => (
                <Grid
                  item
                  key={index}
                  className={classes.scaleNb}
                  style={{
                    width: index === gridNb ? "" : `${gridSpace + 1}px`,
                    minWidth: index === gridNb ? "" : `${gridSpace + 1}px`,
                  }}
                >
                  {(index * maxScaleValue) / gridNb}
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item style={{ textAlign: "right" }}>
            {I18n.get("dashboard_vehicles")}
          </Grid>
        </Grid>
      </Grid>
    </DashboardCard>
  );
};

export default withStyles(styles)(MdcChart);
